import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../App';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ExportInvoiceModal from '../components/ExportInvoiceModal';
import { FileText, CheckCircle, Clock, AlertCircle, ArrowRight, Plus, X, AlertTriangle, DollarSign, Download } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    additionalInfo: 0,
    overdue: 0,
    due: 0,
    outstanding: 0,
    onHold: 0,
    paid: 0
  });
  const [loading, setLoading] = useState(true);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  useEffect(() => {
    const userData = getUser();
    setUser(userData);
    if (userData) {
      fetchStats(userData);
    }
  }, []);

  const fetchStats = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      if (userData.role === 'requester') {
        const response = await axios.get(`${API}/purchase-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // API returns array directly
        const requests = Array.isArray(response.data) ? response.data : (response.data.purchase_requests || []);
        
        // Approved: includes paid status
        const approved = requests.filter(r => r.status === 'paid').length;
        
        // Rejected: rejected by finance head
        const rejected = requests.filter(r => r.status === 'rejected').length;
        
        // Pending: all statuses except rejected and paid
        const pending = requests.filter(r => r.status !== 'rejected' && r.status !== 'paid').length;
        
        setStats({
          approved: approved,
          rejected: rejected,
          pending: pending,
          total: approved + rejected + pending // Sum of all three
        });
      } else if (userData.role === 'function_approver') {
        const response = await axios.get(`${API}/purchase-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const requests = Array.isArray(response.data) ? response.data : (response.data.purchase_requests || []);
        const userEmail = userData.email;
        const pending = requests.filter(r => r.status === 'pending_approval').length;
        const approved = requests.filter(r => r.approved_by === userEmail && r.status !== 'pending_approval').length;
        const rejected = requests.filter(r => r.status === 'rejected' && r.rejected_by === userEmail).length;
        setStats({
          total: pending + approved + rejected, // Total actions: pending + approved + rejected
          pending: pending,
          approved: approved,
          rejected: rejected
        });
      } else if (userData.role === 'accounts') {
        const response = await axios.get(`${API}/purchase-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const requests = Array.isArray(response.data) ? response.data : (response.data.purchase_requests || []);
        const pending = requests.filter(r => r.status === 'pending_finance_approval').length;
        const approved = requests.filter(r => r.status === 'tax_invoice_uploaded').length;
        const additionalInfo = requests.filter(r => r.status === 'additional_info_requested').length;
        setStats({
          total: pending + approved + additionalInfo, // Sum of pending + approved + additional info
          pending: pending,
          approved: approved,
          rejected: additionalInfo // Using rejected field for "Addl Info Requested"
        });
      } else if (userData.role === 'finance_head') {
        const response = await axios.get(`${API}/purchase-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const requests = Array.isArray(response.data) ? response.data : (response.data.purchase_requests || []);
        
        const pending = requests.filter(r => 
          r.status === 'pending_finance_approval' ||
          (r.is_advance_payment && r.advance_payment_status === 'pending_ppa')
        ).length;
        
        const approved = requests.filter(r => 
          r.status === 'finance_approved' || 
          r.status === 'paid' ||
          (r.is_advance_payment && r.advance_payment_status === 'approved_pp') ||
          (r.is_advance_payment && r.is_partial_paid && r.status !== 'pending_finance_approval')
        ).length;
        
        const rejected = requests.filter(r => 
          r.status === 'rejected' && r.rejected_by && r.rejected_by.includes('finance')
        ).length;
        
        setStats({
          pending: pending,
          approved: approved,
          rejected: rejected,
          total: pending + approved + rejected // Sum of the three stats
        });
      } else if (userData.role === 'finance_team') {
        // Fetch payment queue data
        const [overdueRes, dueRes, outstandingRes, onHoldRes, paidRes] = await Promise.all([
          axios.get(`${API}/payments/overdue`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/payments/due`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/payments/outstanding`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/payments/on-hold`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/payments/history`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        
        // Use backend counts directly - backend queries are the source of truth
        const overdueCount = overdueRes.data.length;
        const dueCount = dueRes.data.length;
        const outstandingCount = outstandingRes.data.length;
        const onHoldCount = onHoldRes.data.length;
        // Use backend count directly - backend query handles all paid scenarios
        const paidCount = paidRes.data.length;
        
        setStats({
          overdue: overdueCount,
          due: dueCount,
          outstanding: outstandingCount,
          onHold: onHoldCount,
          paid: paidCount
        });
      } else if (userData.role === 'admin') {
        const response = await axios.get(`${API}/purchase-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allRequests = Array.isArray(response.data) ? response.data : (response.data.purchase_requests || []);
        
        const paidCount = allRequests.filter(r => r.status === 'paid').length;
        const approvedCount = allRequests.filter(r => r.status === 'finance_approved').length;
        const rejectedCount = allRequests.filter(r => 
          r.status === 'rejected' && r.rejected_by && r.rejected_by.includes('finance')
        ).length;
        const pendingCount = allRequests.filter(r => 
          r.status !== 'paid' && 
          r.status !== 'finance_approved' && 
          !(r.status === 'rejected' && r.rejected_by && r.rejected_by.includes('finance'))
        ).length;
        const onHoldCount = allRequests.filter(r => r.hold_status === true).length;
        
        setStats({
          paid: paidCount,
          approved: approvedCount,
          rejected: rejectedCount,
          pending: pendingCount,
          total: paidCount + approvedCount + rejectedCount + pendingCount,
          onHold: onHoldCount
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleGreeting = () => {
    const greetings = {
      'requester': 'Create and track your purchase requests',
      'function_approver': 'Review and approve purchase requests',
      'finance_head': 'Review financial documentation and approvals',
      'finance_team': 'Manage payment queue and process payments',
      'accounts': 'Verify documents and manage vendor payments',
      'admin': 'Manage users and oversee all operations'
    };
    return greetings[user?.role] || 'Welcome to Corp Pay';
  };

  const getQuickActions = () => {
    const actions = {
      'requester': [
        { label: 'New Purchase Request', path: '/purchase-requests', icon: Plus, color: 'bg-indigo-600' },
        { label: 'View My Requests', path: '/purchase-requests', icon: FileText, color: 'bg-blue-600' },
        { label: 'Manage Vendors', path: '/vendors', icon: FileText, color: 'bg-green-600' }
      ],
      'function_approver': [
        { label: 'Pending Approvals', path: '/approvals', icon: Clock, color: 'bg-orange-600' },
        { label: 'View All Requests', path: '/approvals', icon: FileText, color: 'bg-blue-600' },
        { label: 'Manage Vendors', path: '/vendors', icon: FileText, color: 'bg-green-600' }
      ],
      'finance_head': [
        { label: 'Pending Review', path: '/finance', icon: Clock, color: 'bg-orange-600' },
        { label: 'View All Payments', path: '/finance', icon: FileText, color: 'bg-blue-600' },
        { label: 'Manage Vendors', path: '/vendors', icon: FileText, color: 'bg-green-600' }
      ],
      'finance_team': [
        { label: 'Payment Queue', path: '/payments', icon: Clock, color: 'bg-orange-600' },
        { label: 'View Payments', path: '/payments', icon: FileText, color: 'bg-blue-600' },
        { label: 'Manage Vendors', path: '/vendors', icon: FileText, color: 'bg-green-600' }
      ],
      'accounts': [
        { label: 'Review Documents', path: '/accounts/purchase-requests', icon: FileText, color: 'bg-blue-600' },
        { label: 'View All Requests', path: '/accounts/purchase-requests', icon: CheckCircle, color: 'bg-green-600' },
        { label: 'Manage Vendors', path: '/vendors', icon: FileText, color: 'bg-green-600' }
      ],
      'admin': [
        { label: 'User Management', path: '/user-management', icon: FileText, color: 'bg-purple-600' },
        { label: 'Admin Controls', path: '/admin', icon: AlertCircle, color: 'bg-red-600' },
        { label: 'Manage Vendors', path: '/vendors', icon: FileText, color: 'bg-green-600' }
      ]
    };
    return actions[user?.role] || [];
  };

  const canExportInvoices = ['finance_head', 'finance_team', 'admin'].includes(user?.role);

  if (!user) return null;

  const quickActions = getQuickActions();

  return (
    <div>
      {/* Welcome Section */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Welcome back, {user.name}!
          </h1>
          <p className="text-gray-600">{getRoleGreeting()}</p>
        </div>
        {canExportInvoices && (
          <Button onClick={() => setExportModalOpen(true)} className="bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
            <Download className="w-4 h-4 mr-2" />
            Export Invoice
          </Button>
        )}
      </div>

      {canExportInvoices && (
        <ExportInvoiceModal open={exportModalOpen} onOpenChange={setExportModalOpen} />
      )}

      {/* Stats Cards - For Requester, Function Approver and Accounts (4 cards) */}
      {(user.role === 'requester' || user.role === 'function_approver' || user.role === 'accounts') && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">
                    {user.role === 'requester' ? 'Pending' : user.role === 'accounts' ? 'Pending PRs' : 'Pending Approvals'}
                  </p>
                  <p className="text-3xl font-bold text-orange-900 mt-2">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">
                    {user.role === 'requester' ? 'Approved' : user.role === 'accounts' ? 'Approved' : 'Total Approved'}
                  </p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{stats.approved}</p>
                </div>
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">
                    {user.role === 'requester' ? 'Rejected' : user.role === 'accounts' ? 'Addl Info Requested' : 'Total Rejected'}
                  </p>
                  <p className="text-3xl font-bold text-red-900 mt-2">{stats.rejected}</p>
                </div>
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  {user.role === 'accounts' ? <AlertCircle className="w-6 h-6 text-white" /> : <X className="w-6 h-6 text-white" />}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-indigo-600">
                    {user.role === 'requester' ? 'Total Requests' : user.role === 'accounts' ? 'Total Requests' : 'Total Actions'}
                  </p>
                  <p className="text-3xl font-bold text-indigo-900 mt-2">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Cards - For Finance Head (4 cards) */}
      {user.role === 'finance_head' && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Pending Reviews</p>
                  <p className="text-3xl font-bold text-orange-900 mt-2">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Approved</p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{stats.approved}</p>
                </div>
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Rejected</p>
                  <p className="text-3xl font-bold text-red-900 mt-2">{stats.rejected}</p>
                </div>
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-indigo-600">Total Requests</p>
                  <p className="text-3xl font-bold text-indigo-900 mt-2">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Cards - For Finance Team (5 cards) */}
      {user.role === 'finance_team' && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Overdue</p>
                  <p className="text-3xl font-bold text-red-900 mt-2">{stats.overdue}</p>
                </div>
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Due</p>
                  <p className="text-3xl font-bold text-orange-900 mt-2">{stats.due}</p>
                </div>
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Outstanding</p>
                  <p className="text-3xl font-bold text-blue-900 mt-2">{stats.outstanding}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">On Hold</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.onHold}</p>
                </div>
                <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Paid</p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{stats.paid}</p>
                </div>
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Cards - For Admin (6 cards) */}
      {user.role === 'admin' && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Paid</p>
                  <p className="text-3xl font-bold text-blue-900 mt-2">{stats.paid}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Approved</p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{stats.approved}</p>
                </div>
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Rejected</p>
                  <p className="text-3xl font-bold text-red-900 mt-2">{stats.rejected}</p>
                </div>
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Pending</p>
                  <p className="text-3xl font-bold text-orange-900 mt-2">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-indigo-600">Total Requests</p>
                  <p className="text-3xl font-bold text-indigo-900 mt-2">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">On Hold</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.onHold}</p>
                </div>
                <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(action.path)}
                  className={`${action.color} text-white p-6 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-between group`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{action.label}</span>
                  </div>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
